import React, { ReactNode } from "react";
import { intl } from "LocaleWrapper";
import { darken, Skeleton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, useTheme } from "@mui/material";
import { Pagination } from "@cofano/cck";
import "./style.scss";

export type GroupedTableColumn<T,> = {
	id: string;
	title: string;
	render?: (row: T) => ReactNode;
	minWidth?: number;
	width?: number;
	maxWidth?: number | string;
	align?: "left" | "center" | "right";
}

export type GroupedTableGroup<T,> = {
	id: number;
	backgroundColor?: string;
	render: () =>  ReactNode;
	rows?: T[];
	subGroups?: GroupedTableSubGroup<T>[]
};

export type GroupedTableSubGroup<T,> = {
	id: number;
	backgroundColor?: string;
	render: () =>  ReactNode;
	renderCells?: (columns: GroupedTableColumn<T>[]) => { content: ReactNode; colSpan?: number }[];
	onClick?: () => void;
	rows: T[];
};

export type GroupedTablePaginationSettings = {
	page?: number;
	size?: number;
	goToPage?: (page: number, size: number) => void;
	totalResults?: number;
	paginationEnabled: boolean;
}

const HeaderRow = <T,>({ columns, headerInFront }: { columns: GroupedTableColumn<T>[], headerInFront: boolean }) => {
	return (
		<TableRow>
			{!headerInFront && (
				<TableCell />
			)}

			{columns.map((column) => (
				<TableCell style={{ fontSize: 13, textAlign: column.align }} id={column.id}>
					{column.title}
				</TableCell>
			))}
		</TableRow>
	);
};

const LoadingRows = ({ size }: { size: number }) => {
	return (
		<React.Fragment>
			{[ ...Array(size) ].map((e, index) => (
				<TableRow key={index}>
					<TableCell colSpan={100}>
						<Skeleton variant="text" sx={{ fontSize: "1rem" }} />
					</TableCell>
				</TableRow>))}
		</React.Fragment>
	);
};

const GroupRow = <T,>({ column, group, headerInFront }: { column: GroupedTableColumn<T>, group: GroupedTableGroup<T>, headerInFront: boolean }) => {
	const theme = useTheme();

	const colSpan = headerInFront ? 1 : 99;

	// Calculate how many rows will be spanned by the group row
	const numberOfRows = ((group.subGroups?.length) || 0) + (group.subGroups?.map(x => x.rows.length).reduce((a, b) => a + b, 0) || 0);
	const rowSpan = headerInFront ? (numberOfRows + 1 ) : 1;

	return (
		<TableRow
			key={group.id}
		>
			<TableCell
				style={{
					fontSize: 13,
					backgroundColor: group.backgroundColor ? theme.palette.mode === "dark" ? darken(group.backgroundColor, 0.8) : group.backgroundColor : undefined,
					verticalAlign: headerInFront ? "top" : "center",
					borderRight: headerInFront ? "2px solid rgb(224, 224, 224)" : "inherit",
					width: column.width,
				}}
				colSpan={colSpan}
				rowSpan={rowSpan}>
				{group.render()}
			</TableCell>

			{headerInFront && group.subGroups?.length == 0 &&  (
				<TableCell colSpan={99}></TableCell>
			)}
		</TableRow>
	);
};

const SubGroupRow = <T,>({ subGroup, columns, headerInFront }: { subGroup: GroupedTableSubGroup<T>, columns: GroupedTableColumn<T>[], headerInFront: boolean }) => {
	const theme = useTheme();
	const cells = subGroup.renderCells?.(columns);

	if (cells) {
		return (
			<TableRow
				hover={!!subGroup.onClick}
				onClick={subGroup.onClick}
				style={{
					backgroundColor: subGroup.backgroundColor ? theme.palette.mode === "dark" ? darken(subGroup.backgroundColor, 0.7) : subGroup.backgroundColor : undefined,
					cursor: subGroup.onClick ? "pointer" : undefined,
				}}
				key={subGroup.id}>
				{!headerInFront && <TableCell />}
				{cells.map((cell, i) => {
					const colIndex = cells.slice(0, i).reduce((acc, c) => acc + (c.colSpan ?? 1), 0);
					return (
						<TableCell key={i} colSpan={cell.colSpan ?? 1} style={{ fontSize: 13, textAlign: columns[colIndex]?.align }}>
							{cell.content}
						</TableCell>
					);
				})}
			</TableRow>
		);
	}

	return (
		<TableRow
			hover={!!subGroup.onClick}
			onClick={subGroup.onClick}
			style={{
				backgroundColor: subGroup.backgroundColor ? theme.palette.mode === "dark" ? darken(subGroup.backgroundColor, 0.7) : subGroup.backgroundColor : undefined,
				cursor: subGroup.onClick ? "pointer" : undefined
			}}
			key={subGroup.id}>
			{!headerInFront && <TableCell />}
			<TableCell colSpan={99} style={{ fontSize: 13 }}>
				{subGroup.render()}
			</TableCell>
		</TableRow>
	);
};

const BodyRows = <T,>({
	rows,
	getEntityId,
	columns,
	headerInFront,
}: {
	rows: T[];
	getEntityId: (row: T) => number;
	columns: GroupedTableColumn<T>[]
	headerInFront: boolean;
}) => {
	return (
		<React.Fragment>
			{rows.map((row) => (
				<TableRow key={getEntityId(row)}>
					{!headerInFront && <TableCell />}
					{columns
						.filter(column => column.render)
						.map((column) => (
							<TableCell style={{ fontSize: 13, width: column.width, minWidth: column.minWidth, maxWidth: column.maxWidth, textAlign: column.align }} id={column.id}>
								{column.render?.(row)}
							</TableCell>
						))}
				</TableRow>
			))}
		</React.Fragment>
	);
};

type GroupSectionProps<T,> = {
	group: GroupedTableGroup<T>;
	columns: GroupedTableColumn<T>[];
	getEntityId: (row: T) => number;
	headerInFront: boolean;
	renderKey?: string | number | boolean;
};

const GroupSectionInner = <T,>({ group, columns, getEntityId, headerInFront }: GroupSectionProps<T>) => {
	return (
		<React.Fragment>
			<GroupRow column={columns[0]} group={group} headerInFront={headerInFront} />

			{group.subGroups?.map((subGroup) => (
				<React.Fragment key={subGroup.id}>
					<SubGroupRow subGroup={subGroup} columns={columns} headerInFront={headerInFront} />

					<BodyRows
						rows={subGroup.rows}
						getEntityId={getEntityId}
						columns={columns}
						headerInFront={headerInFront}
					/>
				</React.Fragment>
			))}

			<BodyRows
				rows={group.rows || []}
				getEntityId={getEntityId}
				columns={columns}
				headerInFront={headerInFront}
			/>
		</React.Fragment>
	);
};

// Re-render a group section when the group changes, or when renderKey changes
const GroupSection = React.memo(GroupSectionInner, (prev, next) => prev.group === next.group && prev.renderKey === next.renderKey) as typeof GroupSectionInner;

const GroupedTable = <T,>({
	contentLoading,
	paginationSettings,
	getEntityId,
	columns,
	groups,
	headerInFront,
	size,
	spacingBetweenGroups,
	renderKey,
}: {
	contentLoading?: boolean;
	paginationSettings: GroupedTablePaginationSettings;
	getEntityId: (row: T) => number;
	columns: GroupedTableColumn<T>[];
	groups: GroupedTableGroup<T>[];
	headerInFront: boolean;
	size: "medium" | "small" | "compact",
	spacingBetweenGroups: number;
	renderKey?: string | number | boolean;
}) => {
	const theme = useTheme();
	return (
		<div className={`grouped-table ${size}`}>
			<TableContainer>
				<Table size={size == "compact" ? "small" : size}>
					<TableHead>
						<HeaderRow columns={columns} headerInFront={headerInFront} />
					</TableHead>

					<TableBody>
						{contentLoading && (
							<LoadingRows size={paginationSettings.size ?? 100} />
						)}

						{!contentLoading && groups.length > 0 && (
							<React.Fragment>
								{groups.map((group, index) => (
									<React.Fragment key={group.id}>
										<GroupSection
											group={group}
											columns={columns}
											getEntityId={getEntityId}
											headerInFront={headerInFront}
											renderKey={renderKey}
										/>

										{/* Spacing row */}
										{index < groups.length - 1 && spacingBetweenGroups > 0 && (
											<TableRow>
												<TableCell
													colSpan={100}
													style={{
														height: spacingBetweenGroups,
														padding: 0,
														borderTop: "2px solid #ddd",
														borderBottom: "2px solid #ddd",
														backgroundColor: "transparent",
													}}
												/>
											</TableRow>
										)}
									</React.Fragment>
								))}
							</React.Fragment>
						)}

						{!contentLoading && groups.length === 0 && (
							<TableRow>
								<TableCell colSpan={100} style={{ fontSize: 13, textAlign: "center" }}>
									{intl.formatMessage({ id: "sEmptyTable" })}
								</TableCell>
							</TableRow>
						)}
					</TableBody>
				</Table>
			</TableContainer>

			{paginationSettings.paginationEnabled && (
				<div style={{
					display: "flex",
					justifyContent: "right",
					padding: "8px",
					position: "sticky",
					bottom: "0px",
					background: theme.palette.background.paper,
					zIndex: 1,
				}}>
					<Pagination
						onChange={(newPage, newSize) => paginationSettings.goToPage?.(newPage - 1, newSize)}
						page={paginationSettings.page ? paginationSettings.page + 1 : undefined}
						size={paginationSettings.size}
						totalResults={paginationSettings.totalResults}
					/>
				</div>
			)}
		</div>
	);
};

export default GroupedTable;
